import { type EventRuleDto } from "@crowdcoursing/api/openapi";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

// User-facing strings
const STRINGS = {
  ADD_RULE: "Add Rule",
  CANCEL: "Cancel",
  DELETE_RULE: "Delete rule",
  DESCRIPTION_LABEL: "Description",
  MANAGE_RULES: "Manage Event Rules",
  MOVE_DOWN: "Move rule down",
  MOVE_UP: "Move rule up",
  NAME_LABEL: "Name",
  REORDER_HINT: "Use arrows to reorder rules",
  SAVE: "Save",
} as const;

export interface EventRulesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (rules: EventRuleDto[]) => void;
  rules: EventRuleDto[];
}

export const EventRulesDialog = ({ open, onClose, onSave, rules: initialRules }: EventRulesDialogProps) => {
  const [rules, setRules] = useState<EventRuleDto[]>(initialRules);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    setRules(initialRules);
    setHasChanges(false);
  }, [initialRules, open]);

  const [newRule, setNewRule] = useState<{ name: string; description: string }>({
    description: "",
    name: "",
  });

  const handleAddRule = () => {
    if (newRule.name && newRule.description) {
      setRules([
        ...rules,
        {
          description: newRule.description,
          name: newRule.name,
          order: rules.length,
        },
      ]);
      setNewRule({ description: "", name: "" });
      setHasChanges(true);
    }
  };

  const handleDeleteRule = (index: number) => {
    const newRules = [...rules];
    newRules.splice(index, 1);
    // Update order of remaining rules
    setRules(newRules.map((rule, idx) => ({ ...rule, order: idx })));
    setHasChanges(true);
  };

  const handleMoveRule = (index: number, direction: "up" | "down") => {
    if ((direction === "up" && index === 0) || (direction === "down" && index === rules.length - 1)) {
      return;
    }

    const newRules = [...rules];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    const [movedRule] = newRules.splice(index, 1);
    newRules.splice(newIndex, 0, movedRule);

    // Update order of all rules
    setRules(newRules.map((rule, idx) => ({ ...rule, order: idx })));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(rules);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{STRINGS.MANAGE_RULES}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="textSecondary">
            {STRINGS.REORDER_HINT}
          </Typography>
        </Box>
        <List>
          {rules.map((rule, index) => (
            <ListItem
              key={rule.name}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 2,
              }}
            >
              <ListItemText primary={rule.name} secondary={rule.description} />
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleMoveRule(index, "up")}
                  disabled={index === 0}
                  aria-label={STRINGS.MOVE_UP}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveRule(index, "down")}
                  disabled={index === rules.length - 1}
                  aria-label={STRINGS.MOVE_DOWN}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={STRINGS.DELETE_RULE}
                  onClick={() => handleDeleteRule(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label={STRINGS.NAME_LABEL}
            value={newRule.name}
            onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={STRINGS.DESCRIPTION_LABEL}
            value={newRule.description}
            onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
            multiline
            rows={2}
          />
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddRule}
            disabled={!newRule.name || !newRule.description}
            sx={{ mt: 1 }}
          >
            {STRINGS.ADD_RULE}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{STRINGS.CANCEL}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!hasChanges}>
          {STRINGS.SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
